import React from "react";
import Select from "react-select";
import { FormGroup } from "reactstrap";

import { DisplaySettingsFormik } from "../lib/useDisplaySettingsForm";
import { SensorEntitlement } from "../../../../../api/grpc/workplaces/workplaces";
import { useToggleModal } from "../../../../../lib/hooks/useToggleModal";
import { ContactSalesModal } from "../../../../ContactSales/ContactSalesModal";
import { Switch } from "../../../Switch/Switch";
import { LabelWithInformationBox } from "../../../LabelWithInformationBox/LabelWithInformationBox";
import { RadioButton } from "../../../RadioButton/RadioButton";
import { selectStyles } from "../../../../../lib/utils/selectStyles";
import { tooltips } from "../../../../../lib/utils/tooltips";
import {
  autoBookingDurationOptions,
  autoReleaseDelayOptions,
  customPresetDefaults,
  motionTriggerSensitivityOptions,
  presetDefaults,
  presetOptions,
  vacancyCoolDownOptions,
} from "./constants";
import type { MotionSensorPreset, SelectOption } from "./constants";

import "./MotionSensorSetting.scss";

interface Props {
  formik: DisplaySettingsFormik;
  onChange: (
    key: string,
    value: string | string[] | number | number[] | boolean | Date | null
  ) => void;
}

const MotionSensorSelect = ({
  fieldName,
  options,
  value,
  disabled,
  onChange,
}: {
  fieldName: string;
  options: SelectOption[];
  value: string | number;
  disabled?: boolean;
  onChange: (field: string, value: string | number) => void;
}) => {
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Select
      options={options}
      theme={selectStyles}
      name={fieldName}
      isMulti={false}
      isSearchable={false}
      isClearable={false}
      isDisabled={disabled}
      onChange={(option) => {
        if (!option || option.value === undefined) {
          return;
        }

        onChange(fieldName, option.value);
      }}
      value={selectedOption || options[0]}
    />
  );
};

const clonePresetSettings = (
  settings: MotionSensorPreset
): MotionSensorPreset => ({ ...settings });

export const MotionSensorSetting = ({ formik, onChange }: Props) => {
  const { isOpen, toggleModal } = useToggleModal();
  const currentSettings =
    formik.values.reservation.motionSensorAutomation || customPresetDefaults;
  const isCustomPreset = currentSettings.preset === "custom";
  const isNoEntitlement =
    formik.values.reservation.sensorEntitlement ===
    SensorEntitlement.NO_ENTITLEMENT;

  return (
    <>
      <Switch
        title="Motion sensor automation"
        message={
          tooltips.rooms.reservation.motionSensor.motionSensorAutomation
        }
        name="reservation.motionSensorAutomation"
        description={
          <>
            Enables motion-based automation when a compatible sensor is
            connected to the Goget One room panel.{" "}
            {isNoEntitlement && (
              <>
                <br></br>
                <strong>Important!</strong> If the toggle is disabled, Goget
                Sales need to activate the feature. Contact Goget Sales{" "}
                <button
                  type="button"
                  className="MotionSensorSetting__contactSalesButton"
                  onClick={toggleModal}
                >
                  here
                </button>
                .
              </>
            )}
          </>
        }
        inputDisabled={isNoEntitlement}
        value={!!formik.values.reservation.motionSensorAutomation}
        onChange={(value) => {
          if (!value) {
            formik.setFieldValue("reservation.motionSensorAutomation", null);
            formik.setFieldTouched("reservation.motionSensorAutomation", true);
            return;
          }

          const savedSettings =
            formik.initialValues.reservation.motionSensorAutomation;
          formik.setFieldValue(
            "reservation.motionSensorAutomation",
            clonePresetSettings(savedSettings || customPresetDefaults)
          );
          formik.setFieldTouched("reservation.motionSensorAutomation", true);
        }}
        className="MotionSensorSetting__switch mb-3"
      >
        <div className="MotionSensorSetting">
          <LabelWithInformationBox
            title="Room type automation presets"
            message={tooltips.rooms.reservation.motionSensor.motionSensorPreset}
            className="mb-3"
          />
          <div className="MotionSensorSetting__presets">
            {presetOptions.map((option) => (
              <RadioButton
                key={option.value}
                title={option.title}
                name="reservation.motionSensorAutomation.preset"
                checked={currentSettings.preset === option.value}
                onChange={(e) => {
                  const selectedPreset = e.target.value;

                  if (selectedPreset === "custom") {
                    const savedCustomSettings =
                      formik.initialValues.reservation.motionSensorAutomation;
                    const customSettings =
                      savedCustomSettings?.preset === "custom"
                        ? savedCustomSettings
                        : customPresetDefaults;
                    formik.setFieldValue(
                      "reservation.motionSensorAutomation",
                      clonePresetSettings(customSettings)
                    );
                    return;
                  }

                  const selectedPresetDefaults =
                    presetDefaults[selectedPreset] || customPresetDefaults;

                  formik.setFieldValue(
                    "reservation.motionSensorAutomation",
                    clonePresetSettings(selectedPresetDefaults)
                  );
                }}
                value={option.value}
                className="pr-3"
              />
            ))}
          </div>

          <FormGroup className="MotionSensorSetting__row">
            <LabelWithInformationBox
              title="Motion trigger sensitivity"
              for="reservation.motionSensorAutomation.motionTriggerSensitivity"
              message={
                tooltips.rooms.reservation.motionSensor.motionTriggerSensitivity
              }
            />
            <div className="MotionSensorSetting__select">
              <MotionSensorSelect
                fieldName="reservation.motionSensorAutomation.motionTriggerSensitivity"
                value={currentSettings.motionTriggerSensitivity}
                options={motionTriggerSensitivityOptions}
                disabled={!isCustomPreset}
                onChange={onChange}
              />
            </div>
          </FormGroup>

          <FormGroup className="MotionSensorSetting__row">
            <LabelWithInformationBox
              title="Auto-release delay"
              for="reservation.motionSensorAutomation.autoReleaseDelay"
              message={tooltips.rooms.reservation.motionSensor.autoReleaseDelay}
            />
            <div className="MotionSensorSetting__select">
              <MotionSensorSelect
                fieldName="reservation.motionSensorAutomation.autoReleaseDelay"
                value={currentSettings.autoReleaseDelay}
                options={autoReleaseDelayOptions}
                disabled={!isCustomPreset}
                onChange={onChange}
              />
            </div>
          </FormGroup>

          <FormGroup className="MotionSensorSetting__row">
            <LabelWithInformationBox
              title="Vacancy cool-down"
              for="reservation.motionSensorAutomation.vacancyCoolDown"
              message={tooltips.rooms.reservation.motionSensor.cooldown}
            />
            <div className="MotionSensorSetting__select">
              <MotionSensorSelect
                fieldName="reservation.motionSensorAutomation.vacancyCoolDown"
                value={currentSettings.vacancyCoolDown}
                options={vacancyCoolDownOptions}
                disabled={!isCustomPreset}
                onChange={onChange}
              />
            </div>
          </FormGroup>

          <FormGroup className="MotionSensorSetting__row">
            <LabelWithInformationBox
              title="Auto-booking duration"
              for="reservation.motionSensorAutomation.autoBookingDuration"
              message={
                tooltips.rooms.reservation.motionSensor.autoBookingDuration
              }
            />
            <div className="MotionSensorSetting__select">
              <MotionSensorSelect
                fieldName="reservation.motionSensorAutomation.autoBookingDuration"
                value={currentSettings.autoBookingDuration}
                options={autoBookingDurationOptions}
                disabled={!isCustomPreset}
                onChange={onChange}
              />
            </div>
          </FormGroup>
        </div>
      </Switch>
      {isOpen && <ContactSalesModal isOpen={isOpen} toggle={toggleModal} />}
    </>
  );
};
